import { onSnapshot } from "mobx-state-tree"
import { RootStoreModel } from "./root-store"
import { Environment } from "../environment"
import * as storage from "../../utils/storage"
import _ from "lodash"

/**
 * The key we'll be saving our state as within async storage.
 */
const ROOT_STATE_STORAGE_KEY = "root"

/**
 * Setup the environment that all the models will be sharing.
 *
 * The environment includes other functions that will be picked from some
 * of the models that get created later. This is how we loosly couple things
 * like events between models.
 */
export async function createEnvironment() {
  const env = new Environment()
  await env.setup()
  return env
}

const onLoad = async () => {
  const rootStore = (await storage.load(ROOT_STATE_STORAGE_KEY) || {})
  rootStore.appStore = {
    ...rootStore.appStore,
    farmStoreModels: (rootStore.appStore?.farmStoreModels || [])
  }
  for (let i = 0; i < rootStore.appStore.farmStoreModels.length; i++) {
    rootStore.appStore.farmStoreModels[i] = {
      ...rootStore.appStore.farmStoreModels[i],
      pondStoreModels: (rootStore.appStore.farmStoreModels[i].pondStoreModels || []),
      batchStoreModels: (rootStore.appStore.farmStoreModels[i].batchStoreModels || []),
    }
    for (let j = 0; j < rootStore.appStore.farmStoreModels[i].pondStoreModels.length; j++) {
      for (let k = 0; k < rootStore.appStore.farmStoreModels[i].pondStoreModels[j].cycleStoreModels.length; k++) {
        rootStore.appStore.farmStoreModels[i].pondStoreModels[j].cycleStoreModels[k] = await storage.load(`${ROOT_STATE_STORAGE_KEY}.a.f.${i}.p.${j}.c.${k}`) || {};
      }
    }
    for (let j = 0; j < rootStore.appStore.farmStoreModels[i].batchStoreModels.length; j++) {
      rootStore.appStore.farmStoreModels[i].batchStoreModels[j] = await storage.load(`${ROOT_STATE_STORAGE_KEY}.a.f.${i}.b.${j}`) || {};
    }
  }

  return rootStore;
}

let lastSnapshot: any;

const onSnapshotDebounce = _.debounce(async (snapshot) => {
  const rootStore = {
    ...snapshot,
    appStore: {
      ...snapshot?.appStore,
      farmStoreModels: (snapshot?.appStore?.farmStoreModels || []).map((farmStore) => ({
        ...farmStore,
        pondStoreModels: (farmStore?.pondStoreModels || []).map((pondStore) => ({
          ...pondStore,
          cycleStoreModels: (pondStore?.cycleStoreModels || []).map(() => ({})),
        })),
        batchStoreModels: (farmStore?.batchStoreModels || []).map(() => ({})),
      }))
    }
  }
  await storage.save(ROOT_STATE_STORAGE_KEY, rootStore);

  for (let i = 0; i < rootStore.appStore.farmStoreModels.length; i++) {
    for (let j = 0; j < rootStore.appStore.farmStoreModels[i].pondStoreModels.length; j++) {
      for (let k = 0; k < rootStore.appStore.farmStoreModels[i].pondStoreModels[j].cycleStoreModels.length; k++) {
        const lastCycleStore = JSON.stringify(lastSnapshot?.appStore?.farmStoreModels?.[i]?.pondStoreModels?.[j]?.cycleStoreModels?.[k]);
        const currentCycleStore = JSON.stringify(snapshot?.appStore?.farmStoreModels?.[i]?.pondStoreModels?.[j]?.cycleStoreModels?.[k]);
        if (lastCycleStore !== currentCycleStore) {
          const c = {
            ...snapshot?.appStore?.farmStoreModels[i]?.pondStoreModels[j]?.cycleStoreModels[k] || {},
            waterQualityMultiMeasurements: [],
            feeds: [],
            fastings: [],
            feedingTrayControls: [],
            samplings: [],
            treatments: [],
            harvests: [],
            mortalities: [],
            chemicalMultiMeasurements: [],
            planktonMultiMeasurements: [],
            microbiologyMultiMeasurements: [],
            cycleDiseases: [],
            cyclePredictions: [],
            cycleActuals: [],
            cycleToDos: [],
          }
          await storage.save(`${ROOT_STATE_STORAGE_KEY}.a.f.${i}.p.${j}.c.${k}`, c)
        }
      }
    }
    for (let j = 0; j < rootStore.appStore.farmStoreModels[i].batchStoreModels.length; j++) {
      const lastBatchStore = JSON.stringify(lastSnapshot?.appStore?.farmStoreModels?.[i]?.batchStoreModels?.[j]);
      const currentBatchStore = JSON.stringify(snapshot?.appStore?.farmStoreModels?.[i]?.batchStoreModels?.[j]);
      if (lastBatchStore !== currentBatchStore) {
        const c = {
          ...snapshot?.appStore?.farmStoreModels[i]?.batchStoreModels[j] || {},
          cycles: [],
        }
        await storage.save(`${ROOT_STATE_STORAGE_KEY}.a.f.${i}.b.${j}`, c)
      }
    }
  }

  lastSnapshot = snapshot;
}, 3000, { trailing: true });

/**
 * Setup the root state.
 */
export async function setupRootStore() {
  let data: any
  let rootStore: any

  // prepare the environment that will be associated with the RootStore.
  const env = await createEnvironment()
  try {
    // load data from storage
    data = (await onLoad()) || {}
    rootStore = RootStoreModel.create(data, env)
  } catch (e) {
    // if there's any problems loading, then let's at least fallback to an empty state
    // instead of crashing.
    rootStore = RootStoreModel.create({}, env)

    // but please inform us what happened
    __DEV__ && console.tron.error(e.message, null)
  }

  // reactotron logging
  if (__DEV__) {
    env.reactotron.setRootStore(rootStore, data)
  }

  // track changes & save to storage
  onSnapshot(rootStore, onSnapshotDebounce)

  return rootStore
}

export async function readRawRootStore() {
  let data: any
  let rootStore: any

  const env = await createEnvironment()
  try {
    // load data from storage
    data = (await onLoad()) || {}
    rootStore = RootStoreModel.create(data, env)
  } catch (e) {
    //
  }

  // track changes & save to storage
  onSnapshot(rootStore, onSnapshotDebounce)

  return rootStore
}
